/**
 * イベントカテゴリ定義（固定）
 * - 目的: フロント側の表示/ルーティング/バリデーションを一箇所で統一する
 *
 * 廃止の考え方:
 * - 開催しなくなった分類は EVENT_CATEGORIES から RETIRED_EVENT_CATEGORIES へ移す
 * - 「新規に選べる／絞り込みに出す」は EVENT_CATEGORIES だけ
 * - 「保存してよい値か」は isAcceptedEventCategory（廃止分も含む）で判定する
 * - 許可リストごと消すと、その分類で登録済みの過去イベントが次の保存で弾かれ、
 *   日時や本文の訂正すらできなくなるため、2段に分けている
 */
export const EVENT_CATEGORIES = ['お茶会', '部活', 'その他'] as const;

/**
 * 廃止したカテゴリ（新規には選べないが、過去のイベントが持っている値）
 * - 「お～いお茶会」: 2026-09-20 に廃止。開催しなくなったため
 */
export const RETIRED_EVENT_CATEGORIES = ['お～いお茶会'] as const;

export type EventCategory = (typeof EVENT_CATEGORIES)[number];
export type RetiredEventCategory = (typeof RETIRED_EVENT_CATEGORIES)[number];

/** 保存してよい値（今選べるもの＋過去データが持つもの） */
export type AcceptedEventCategory = EventCategory | RetiredEventCategory;

/**
 * イベントカテゴリを正規化
 * - 目的: URLクエリなど不正値が入っても安全に扱う
 * - 引数: input (string|null|undefined)
 * - 戻り値: EventCategory | null（未指定/不正値/廃止済みは null）
 *
 * 注: 廃止済みは絞り込みのチップが無いため null に倒す。
 *     古いURLを開いても 404 にはせず「絞り込み無し」として全件を出す。
 */
export function normalizeEventCategory(
  input?: string | null
): EventCategory | null {
  const v = (input ?? '').trim();
  if (!v) return null;
  return (EVENT_CATEGORIES as readonly string[]).includes(v)
    ? (v as EventCategory)
    : null;
}

/**
 * そのまま保存してよいカテゴリか
 * - 目的: 過去のイベントを開いて保存しても、分類が勝手に書き換わらないようにする
 */
export function isAcceptedEventCategory(
  input?: string | null
): input is AcceptedEventCategory {
  const v = (input ?? '').trim();
  if (!v) return false;
  return (
    (EVENT_CATEGORIES as readonly string[]).includes(v) ||
    (RETIRED_EVENT_CATEGORIES as readonly string[]).includes(v)
  );
}
